import { cn } from '@/lib/utils';

interface NeuPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Optional summary text shown on the left (e.g. "42 registros en total"). */
  summary?: string;
  className?: string;
}

/**
 * Neumorphic pagination control with Anterior / Siguiente buttons
 * and a "Pagina X de Y" indicator.
 */
export function NeuPagination({
  page,
  totalPages,
  onPageChange,
  summary,
  className,
}: NeuPaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <nav
      aria-label="Paginacion"
      className={cn('flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between', className)}
    >
      {summary ? (
        <p className="text-sm text-sime-text-tertiary text-center sm:text-left">{summary}</p>
      ) : (
        <span className="hidden sm:inline" />
      )}
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => { onPageChange(Math.max(1, page - 1)); }}
          disabled={page <= 1}
          className={cn(
            'px-4 py-2 min-h-[44px] rounded-xl text-sm font-medium transition-all duration-150',
            'text-sime-text-secondary',
            'shadow-neu-sm',
            'hover:shadow-neu-xs',
            'active:shadow-neu-inset-sm',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:shadow-neu-sm',
          )}
          aria-label="Pagina anterior"
        >
          Anterior
        </button>
        <span className="text-sm text-sime-text-primary font-medium px-2 select-none whitespace-nowrap">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => { onPageChange(Math.min(totalPages, page + 1)); }}
          disabled={page >= totalPages}
          className={cn(
            'px-4 py-2 min-h-[44px] rounded-xl text-sm font-medium transition-all duration-150',
            'text-sime-text-secondary',
            'shadow-neu-sm',
            'hover:shadow-neu-xs',
            'active:shadow-neu-inset-sm',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:shadow-neu-sm',
          )}
          aria-label="Pagina siguiente"
        >
          Siguiente
        </button>
      </div>
    </nav>
  );
}
